// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import Channels from '@app/features/channel/state/Channels';
import GuildNSFWAgree from '@app/features/guild/state/GuildNSFWAgree';
import Guilds from '@app/features/guild/state/Guilds';
import {Message, messageMentionsCurrentUser} from '@app/features/messaging/models/Message';
import type {ReactionEmoji} from '@app/features/messaging/utils/ReactionUtils';
import {makeSyncedField} from '@app/features/user/state/SyncedField';
import {ChannelTypes} from '@app/vendor/constants/ChannelConstants';
import type {Channel} from '@app/vendor/schema/domains/channel/ChannelSchemas';
import type {Message as WireMessage} from '@app/vendor/schema/domains/message/MessageResponseSchemas';
import {RecentMentionsSettingsSchema} from '@app/vendor/schema/gen/fluxer/user/preferences/v1/preferences_pb';
import {makeAutoObservable} from 'mobx';
export interface MentionFilters {
	includeEveryone: boolean;
	includeRoles: boolean;
	includeGuilds: boolean;
}
const DEFAULT_FILTERS: MentionFilters = {
	includeEveryone: true,
	includeRoles: true,
	includeGuilds: true,
};
class MentionFeed {
	recentMentions: Array<Message> = [];
	fetched = false;
	hasMore = true;
	isLoadingMore = false;
	filters: MentionFilters = {...DEFAULT_FILTERS};
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.initPersistence();
	}
	private async initPersistence(): Promise<void> {
		await makeSyncedField(this, {
			field: 'recentMentions',
			schema: RecentMentionsSettingsSchema,
			persist: ['filters'],
			toMessage: (s) => ({
				includeEveryone:
					s.filters.includeEveryone === DEFAULT_FILTERS.includeEveryone ? undefined : s.filters.includeEveryone,
				includeRoles: s.filters.includeRoles === DEFAULT_FILTERS.includeRoles ? undefined : s.filters.includeRoles,
				includeGuilds: s.filters.includeGuilds === DEFAULT_FILTERS.includeGuilds ? undefined : s.filters.includeGuilds,
			}),
			applyMessage: (s, m) => {
				s.filters = {
					includeEveryone: m.includeEveryone ?? DEFAULT_FILTERS.includeEveryone,
					includeRoles: m.includeRoles ?? DEFAULT_FILTERS.includeRoles,
					includeGuilds: m.includeGuilds ?? DEFAULT_FILTERS.includeGuilds,
				};
			},
		});
	}
	getFilters(): MentionFilters {
		return this.filters;
	}
	getHasMore(): boolean {
		return this.hasMore;
	}
	getIsLoadingMore(): boolean {
		return this.isLoadingMore;
	}
	getAccessibleMentions(): ReadonlyArray<Message> {
		return this.recentMentions.filter((message) => this.isMessageAccessible(message));
	}
	private isMessageAccessible(message: Message): boolean {
		const channel = Channels.getChannel(message.channelId);
		if (!channel) {
			return false;
		}
		switch (channel.type) {
			case ChannelTypes.DM:
			case ChannelTypes.DM_PERSONAL_NOTES:
				return true;
			case ChannelTypes.GROUP_DM:
				return channel.recipientIds.length > 0;
			case ChannelTypes.GUILD_TEXT:
			case ChannelTypes.GUILD_VOICE: {
				if (!channel.guildId) return false;
				const guild = Guilds.getGuild(channel.guildId);
				return guild != null;
			}
			default:
				return false;
		}
	}
	handleConnectionOpen(): void {
		this.recentMentions = [];
		this.fetched = false;
		this.hasMore = true;
		this.isLoadingMore = false;
	}
	handleFetchPending(): void {
		this.isLoadingMore = true;
	}
	handleRecentMentionsFetchSuccess(messages: ReadonlyArray<WireMessage>): void {
		const filteredMessages = this.filterMessages(messages);
		const isLoadMore = this.isLoadingMore && this.fetched;
		if (isLoadMore) {
			this.recentMentions.push(...filteredMessages.map((m) => new Message(m)));
		} else {
			this.recentMentions = filteredMessages.map((message) => new Message(message));
		}
		this.fetched = true;
		this.hasMore = messages.length === 25;
		this.isLoadingMore = false;
	}
	handleRecentMentionsFetchError(): void {
		this.isLoadingMore = false;
	}
	updateFilters(filters: Partial<MentionFilters>): void {
		this.filters = {...this.filters, ...filters};
		this.fetched = false;
	}
	private filterMessages(messages: ReadonlyArray<WireMessage>): ReadonlyArray<WireMessage> {
		return messages.filter((message) => {
			const channel = Channels.getChannel(message.channel_id);
			if (!channel) return false;
			return !GuildNSFWAgree.shouldShowGate({channelId: channel.id, guildId: channel.guildId ?? null});
		});
	}
	handleChannelDelete(channel: Channel): void {
		this.recentMentions = this.recentMentions.filter((message) => message.channelId !== channel.id);
	}
	handleGuildDelete(guildId: string): void {
		this.recentMentions = this.recentMentions.filter((message) => {
			const channel = Channels.getChannel(message.channelId);
			return !channel || channel.guildId !== guildId;
		});
	}
	handleMessageUpdate(message: WireMessage): void {
		const index = this.recentMentions.findIndex((m) => m.id === message.id);
		if (index === -1) return;
		this.recentMentions[index] = this.recentMentions[index].withUpdates(message);
	}
	handleMessageDelete(messageId: string): void {
		this.recentMentions = this.recentMentions.filter((message) => message.id !== messageId);
	}
	handleMessageCreate(message: WireMessage): void {
		if (!messageMentionsCurrentUser(message)) {
			return;
		}
		const channel = Channels.getChannel(message.channel_id);
		if (!channel) return;
		if (GuildNSFWAgree.shouldShowGate({channelId: channel.id, guildId: channel.guildId ?? null})) {
			return;
		}
		const messageRecord = new Message(message);
		this.recentMentions.unshift(messageRecord);
	}
	private updateMessageWithReaction(messageId: string, updater: (message: Message) => Message): void {
		const index = this.recentMentions.findIndex((m) => m.id === messageId);
		if (index === -1) return;
		this.recentMentions[index] = updater(this.recentMentions[index]);
	}
	handleMessageReactionAdd(messageId: string, userId: string, emoji: ReactionEmoji): void {
		this.updateMessageWithReaction(messageId, (message) =>
			message.withReaction(emoji, true, userId === Authentication.currentUserId),
		);
	}
	handleMessageReactionRemove(messageId: string, userId: string, emoji: ReactionEmoji): void {
		this.updateMessageWithReaction(messageId, (message) =>
			message.withReaction(emoji, false, userId === Authentication.currentUserId),
		);
	}
	handleMessageReactionRemoveAll(messageId: string): void {
		this.updateMessageWithReaction(messageId, (message) => message.withUpdates({reactions: []}));
	}
	handleMessageReactionRemoveEmoji(messageId: string, emoji: ReactionEmoji): void {
		this.updateMessageWithReaction(messageId, (message) => message.withoutReactionEmoji(emoji));
	}
}
export default new MentionFeed();
